import { Auth } from 'aws-amplify';
import React from 'react';

import {
  MutationConfirmation,
  MutationConfirmationResponse,
} from '~/components/AIInsightsSidebar/MutationConfirmation/mutationConfirmation.types';
import { extractCitationText } from '~/components/AIInsightsSidebar/WebSearch/citationUtils';
import { Block } from '~/components/AIInsightsUI/blockTypes';

import {
  A2uiOperation,
  MessageCitation,
  MessagePart,
  MessageSource,
  PdfReport,
  stripLeadingNewlines,
  ThinkingSource,
} from './AIInsightsModal.utils';

export enum Trigger {
  CtaOpen = 'cta_open',
  UserMessage = 'user_message',
}

enum StreamChunkType {
  Text = 'text',
  Thinking = 'thinking',
  Action = 'action',
  Block = 'block',
  Citation = 'citation',
  Sources = 'sources',
  ChartLoading = 'chart_loading',
  A2uiLoading = 'a2ui_loading',
  A2ui = 'a2ui',
  A2uiError = 'a2ui_error',
  MutationConfirmation = 'mutation_confirmation',
  PdfReport = 'pdf_report',
}

const SSE_DATA_PREFIX = 'data: ';
const SSE_DONE_SIGNAL = '[DONE]';
const PAYLOAD_VERSION = '1';

interface RawCitation {
  title: string;
  url?: string;
  sourceNumber?: number;
  location: Record<string, { start?: number; end?: number; documentIndex?: number }>;
  text?: string;
  sourceContent?: string | Array<{ text?: string }>;
}

type StreamChunkEvent =
  | { type: StreamChunkType.Text; content: string }
  | { type: StreamChunkType.Thinking; content: string; url?: string; title?: string }
  | { type: StreamChunkType.Action; action: string }
  | { type: StreamChunkType.Block; block: Block }
  | { type: StreamChunkType.Citation; citation: RawCitation }
  | { type: StreamChunkType.Sources; sources: MessageSource[] }
  | { type: StreamChunkType.ChartLoading }
  | { type: StreamChunkType.A2uiLoading }
  | { type: StreamChunkType.A2ui; a2ui_operations: A2uiOperation[] }
  | { type: StreamChunkType.A2uiError; error: string }
  | ({ type: StreamChunkType.MutationConfirmation } & MutationConfirmation)
  | { type: StreamChunkType.PdfReport; pdf_report: PdfReport };

interface StreamResult {
  parts: MessagePart[];
  actions: string[];
  citations: MessageCitation[];
  sources: MessageSource[];
}

interface UseBedrockStreamParams {
  sessionIdRef: React.MutableRefObject<string | null>;
  abortControllerRef: React.MutableRefObject<AbortController | null>;
  streamingBufferRef: React.MutableRefObject<string>;
  currentLocationDeeplink: string | null;
  onToolStatus?: (status: string | null) => void;
  onBlock?: (block: Block, precedingText: string) => void;
  onThinkingSource?: (source: ThinkingSource) => void;
  onSources?: (sources: MessageSource[]) => void;
  onCitation?: (citation: MessageCitation) => void;
  onChartLoading?: () => void;
  onA2uiLoading?: () => void;
  onA2ui?: (operations: A2uiOperation[], surfaceId: string, precedingText: string) => void;
  onA2uiError?: (error: string) => void;
  onMutationConfirmation?: (confirmation: MutationConfirmation, precedingText: string) => void;
  onPdfReport?: (report: PdfReport, precedingText: string) => void;
}

const refreshToken = async (): Promise<void> => {
  const user = await Auth.currentAuthenticatedUser();
  const currentSession = user.signInUserSession;
  return new Promise((resolve, reject) => {
    user.refreshSession(currentSession.refreshToken, (error) => {
      if (error) {
        reject(error);
      } else {
        resolve();
      }
    });
  });
};

const getBedrockEndpoint = (): string => {
  const localEndpoint = process.env.REACT_APP_AI_INSIGHTS_LOCAL_ENDPOINT;

  if (localEndpoint) {
    return localEndpoint;
  }

  const bedrockEndpointUrl = process.env.REACT_APP_BEDROCK_AGENT_CORE_ENDPOINT_URL;
  const agentArn = process.env.REACT_APP_BEDROCK_AGENT_ARN;

  if (!bedrockEndpointUrl || !agentArn) {
    throw new Error('Bedrock endpoint or agent ARN not configured');
  }

  const escapedAgentArn = encodeURIComponent(agentArn);
  return `${bedrockEndpointUrl}/runtimes/${escapedAgentArn}/invocations?qualifier=DEFAULT`;
};

const sendMessageWithRetry = async (
  payload: Record<string, unknown>,
  sessionIdRef: React.MutableRefObject<string | null>,
  abortControllerRef: React.MutableRefObject<AbortController | null>,
  retryCount = 0,
): Promise<Response> => {
  // Get authentication tokens
  const session = await Auth.currentSession();
  const accessToken = session.getAccessToken().getJwtToken();
  const idToken = session.getIdToken().getJwtToken();

  // Session ID should be set by the modal (section-identifier format)
  if (!sessionIdRef.current) {
    throw new Error('Session ID is not set. Modal should initialize it before sending messages.');
  }

  const origin = window.location.origin;
  const bedrockEndpoint = getBedrockEndpoint();
  const response = await fetch(bedrockEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Id-Token': idToken,
      'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionIdRef.current,
      'X-Amzn-Bedrock-AgentCore-Runtime-Custom-Origin': origin,
      Origin: origin,
    },
    body: JSON.stringify(payload),
    signal: abortControllerRef.current?.signal || undefined,
  });

  // If we get a 401 and haven't retried yet, refresh token and retry
  if (response.status === 401 && retryCount === 0) {
    try {
      await refreshToken();
      return sendMessageWithRetry(payload, sessionIdRef, abortControllerRef, 1);
    } catch (refreshError) {
      console.error('Error refreshing token:', refreshError);
      throw new Error('Failed to refresh authentication token');
    }
  }

  if (!response.ok) {
    throw new Error(`Failed to send message: ${response.status} ${response.statusText}`);
  }

  return response;
};

// The surfaceId (the server-derived form id) lives inside the createSurface op. The agent always
// emits it, so the empty fallback is unreachable; it only keeps the return type total.
const extractSurfaceId = (operations: A2uiOperation[]): string => {
  for (const op of operations) {
    const createSurface = (op as { createSurface?: { surfaceId?: unknown } }).createSurface;
    if (createSurface && typeof createSurface.surfaceId === 'string') {
      return createSurface.surfaceId;
    }
  }
  return '';
};

// Yield through the timer queue because the typewriter itself is timer-driven. scheduler.yield()
// runs its continuation ahead of same-priority tasks, which can repeatedly delay the setInterval
// that paints streamed text while a burst is being drained.
const yieldToMacrotask = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));

const processStreamChunks = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  streamingBufferRef: React.MutableRefObject<string>,
  onToolStatus?: (status: string | null) => void,
  onBlock?: (block: Block, precedingText: string) => void,
  onThinkingSource?: (source: ThinkingSource) => void,
  onSources?: (sources: MessageSource[]) => void,
  onCitation?: (citation: MessageCitation) => void,
  onChartLoading?: () => void,
  onA2uiLoading?: () => void,
  onA2ui?: (operations: A2uiOperation[], surfaceId: string, precedingText: string) => void,
  onA2uiError?: (error: string) => void,
  onMutationConfirmation?: (confirmation: MutationConfirmation, precedingText: string) => void,
  onPdfReport?: (report: PdfReport, precedingText: string) => void,
): Promise<StreamResult> => {
  const decoder = new TextDecoder();
  // Trailing text run currently being animated by the typewriter.
  // Resets to '' each time a block is emitted.
  let assistantMessage = '';
  let buffer = '';
  let hasReceivedText = false;
  let wasThinking = false;
  // Tracks CPU time spent parsing stream lines while discounting time awaiting network reads.
  // Checking this inside the line loop also covers one large chunk containing many SSE events.
  let lastYieldAt = performance.now();
  const MAX_MS_BETWEEN_YIELDS = 16;
  // Increments each time a block fires (assistantMessage resets). Citations are tagged
  // with this index so each text part only receives its own citations at render time.
  let textPartIndex = 0;
  const actions: string[] = [];
  const citations: MessageCitation[] = [];
  const sources: MessageSource[] = [];
  // Ordered sequence of parts built up as the stream arrives.
  const parts: MessagePart[] = [];

  // Iterative drain (not recursion) so the retained frame count stays O(1) over the stream.
  readLoop: for (;;) {
    const readStartedAt = performance.now();
    const { done, value } = await reader.read();
    lastYieldAt += performance.now() - readStartedAt;

    if (done) {
      break;
    }

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (line.trim() === '') continue;

      // Handle Server-Sent Events format - Bedrock sends "data: <json-object>"
      if (line.startsWith(SSE_DATA_PREFIX)) {
        const data = line.slice(SSE_DATA_PREFIX.length).trim();
        if (data === SSE_DONE_SIGNAL) {
          break readLoop;
        }
        try {
          const parsed: unknown = JSON.parse(data);

          if (typeof parsed === 'string') {
            // Backward compatibility: plain quoted strings
            assistantMessage += parsed;
          } else if (parsed && typeof parsed === 'object' && 'type' in parsed) {
            const event = parsed as StreamChunkEvent;
            switch (event.type) {
              case StreamChunkType.Text:
                if (!hasReceivedText || wasThinking) {
                  hasReceivedText = true;
                  wasThinking = false;
                  onToolStatus?.(null);
                }
                assistantMessage += event.content;
                break;
              case StreamChunkType.Thinking:
                wasThinking = true;
                onToolStatus?.(event.content);
                if (event.url) {
                  onThinkingSource?.({ url: event.url, title: event.title ?? '' });
                }
                break;
              case StreamChunkType.Action:
                actions.push(event.action);
                break;
              case StreamChunkType.Block: {
                // Close the trailing text run into a part before the block
                const trailingText = stripLeadingNewlines(assistantMessage);
                if (trailingText) {
                  parts.push({ type: 'text', text: trailingText });
                }
                parts.push({ type: 'block', block: event.block });
                // Reset trailing run so post-chart text streams fresh. Pass the preceding text to
                // onBlock so the live consumer can close the same text part before the reset takes effect.
                assistantMessage = '';
                streamingBufferRef.current = '';
                textPartIndex++;
                onBlock?.(event.block, trailingText);
                break;
              }
              case StreamChunkType.Citation: {
                const { citation: c } = event;
                // Chip is placed at the end of the last text chunk received before this citation event.
                // Use stripLeadingNewlines to match the same basis used by every render path
                // (streamingBufferRef and part.text are both stripped), so the offset never overshoots.
                const streamPosition = stripLeadingNewlines(assistantMessage).length;
                const mappedCitation: MessageCitation = {
                  partIndex: textPartIndex,
                  sourceNumber: c.sourceNumber,
                  url: c.url,
                  title: c.title,
                  start: streamPosition,
                  end: streamPosition,
                  text: extractCitationText(c.text ?? c.sourceContent),
                };
                citations.push(mappedCitation);
                onCitation?.(mappedCitation);
                break;
              }
              case StreamChunkType.Sources:
                onSources?.(event.sources);
                sources.push(...event.sources);
                break;
              case StreamChunkType.ChartLoading:
                onChartLoading?.();
                break;
              case StreamChunkType.A2uiLoading:
                onA2uiLoading?.();
                break;
              case StreamChunkType.A2ui: {
                // Mirror the block flow: close the trailing text run, then emit the surface
                // as its own part so text/surface order is preserved.
                const trailingText = stripLeadingNewlines(assistantMessage);
                if (trailingText) {
                  parts.push({ type: 'text', text: trailingText });
                }
                const operations = event.a2ui_operations;
                const surfaceId = extractSurfaceId(operations);
                parts.push({ type: 'a2ui', operations, surfaceId });
                assistantMessage = '';
                streamingBufferRef.current = '';
                textPartIndex++;
                onA2ui?.(operations, surfaceId, trailingText);
                break;
              }
              case StreamChunkType.A2uiError:
                // The agent auto-retries invalid compositions (a valid `a2ui` usually
                // follows) and falls back to plain text after repeated failures. Surface
                // nothing scary — just clear any loading state. Never crash.
                onA2uiError?.(event.error);
                break;
              case StreamChunkType.MutationConfirmation: {
                const { type: _type, ...confirmation } = event;
                // Close the trailing text run into a part before the card
                const trailingText = stripLeadingNewlines(assistantMessage);
                if (trailingText) {
                  parts.push({ type: 'text', text: trailingText });
                }
                parts.push({ type: 'confirmation', confirmation });
                assistantMessage = '';
                streamingBufferRef.current = '';
                textPartIndex++;
                onMutationConfirmation?.(confirmation, trailingText);
                break;
              }
              case StreamChunkType.PdfReport: {
                // Close the trailing text run into a part before the report, mirroring the Block case.
                const trailingText = stripLeadingNewlines(assistantMessage);
                if (trailingText) {
                  parts.push({ type: 'text', text: trailingText });
                }
                parts.push({ type: 'pdfReport', report: event.pdf_report });
                assistantMessage = '';
                streamingBufferRef.current = '';
                textPartIndex++;
                onPdfReport?.(event.pdf_report, trailingText);
                break;
              }
            }
          }
        } catch {
          // If parsing fails, use the data as-is
          assistantMessage += data;
        }
      } else if (line.trim() && !line.startsWith('event:') && !line.startsWith('id:')) {
        assistantMessage += line;
      }

      streamingBufferRef.current = stripLeadingNewlines(assistantMessage);

      if (performance.now() - lastYieldAt > MAX_MS_BETWEEN_YIELDS) {
        await yieldToMacrotask();
        lastYieldAt = performance.now();
      }
    }
  }

  // Flush any remaining trailing text as the final part
  const trailingText = stripLeadingNewlines(assistantMessage);
  if (trailingText) {
    parts.push({ type: 'text', text: trailingText });
  }

  return { parts, actions, citations, sources };
};

// Sent back to the agent when the user submits an A2UI surface. `formId` is the server-derived id
// the surface was rendered under (== A2UI surfaceId); `values` is the surface's data model;
// `idempotencyKey` is generated once per submit and reused by sendMessageWithRetry's 401-refresh
// retry, so the agent's CreateEvent(clientToken) never writes a duplicate submission event.
export interface A2uiSubmissionPayload {
  formId: string;
  values: Record<string, unknown>;
  idempotencyKey: string;
}

export type TriggerPayload =
  | { prompt: string }
  | { a2uiSubmission: A2uiSubmissionPayload }
  | { interruptResponse: MutationConfirmationResponse }
  | Trigger.CtaOpen;

const buildPayload = (
  triggerPayload: TriggerPayload,
  currentLocationDeeplink: string | null,
): Record<string, unknown> => {
  const isCtaOpen = triggerPayload === Trigger.CtaOpen;
  const trigger = isCtaOpen ? Trigger.CtaOpen : Trigger.UserMessage;
  return {
    version: PAYLOAD_VERSION,
    trigger,
    ...(isCtaOpen ? {} : triggerPayload),
    timezone: String(-new Date().getTimezoneOffset()),
    timestamp: new Date().toISOString(),
    ...(currentLocationDeeplink ? { currentLocationDeeplink } : {}),
  };
};

export const useBedrockStream = ({
  sessionIdRef,
  abortControllerRef,
  streamingBufferRef,
  currentLocationDeeplink,
  onToolStatus,
  onBlock,
  onThinkingSource,
  onSources,
  onCitation,
  onChartLoading,
  onA2uiLoading,
  onA2ui,
  onA2uiError,
  onMutationConfirmation,
  onPdfReport,
}: UseBedrockStreamParams) => {
  const sendMessage = async (payload: TriggerPayload): Promise<StreamResult> => {
    const fullPayload = buildPayload(payload, currentLocationDeeplink);
    const response = await sendMessageWithRetry(fullPayload, sessionIdRef, abortControllerRef);

    const reader = response.body?.getReader();

    if (!reader) {
      throw new Error('No reader available');
    }

    return processStreamChunks(
      reader,
      streamingBufferRef,
      onToolStatus,
      onBlock,
      onThinkingSource,
      onSources,
      onCitation,
      onChartLoading,
      onA2uiLoading,
      onA2ui,
      onA2uiError,
      onMutationConfirmation,
      onPdfReport,
    );
  };

  return { sendMessage };
};
